import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Form, Table, Spinner, Alert, Button } from 'react-bootstrap'
import client from '../../api/client'
import CostChart from '../../components/charts/CostChart'

function Costs() {
  const [costData, setCostData] = useState([])
  const [forecastData, setForecastData] = useState([])
  const [breakdown, setBreakdown] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    dateFrom: getDefaultDateFrom(),
    dateTo: getDefaultDateTo(),
    groupBy: 'day',
  })

  function getDefaultDateFrom() {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0]
  }

  function getDefaultDateTo() {
    return new Date().toISOString().split('T')[0]
  }

  useEffect(() => {
    fetchCostData()
  }, [])

  const fetchCostData = async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      params.append('group_by', filters.groupBy)

      const [costsResponse, forecastResponse, breakdownResponse] = await Promise.all([
        client.get(`/admin/api/v1/costs?${params}`),
        client.get('/admin/api/v1/costs/forecast'),
        client.get(`/admin/api/v1/costs/breakdown?${params}`),
      ])

      setCostData(costsResponse.data.data || [])
      setForecastData(forecastResponse.data.forecast || [])
      setBreakdown(breakdownResponse.data.breakdown || [])
    } catch (err) {
      console.error('Error fetching cost data:', err)
      setError('Erreur lors du chargement des données de coûts')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    fetchCostData()
  }

  // Calculate totals
  const totalCost = costData.reduce((sum, item) => sum + (item.cost || 0), 0)
  const avgDailyCost = costData.length > 0 ? totalCost / costData.length : 0
  const projectedMonthlyCost = avgDailyCost * 30

  // Get forecast total (30 days)
  const forecastTotal = forecastData.reduce((sum, item) => sum + (item.cost || 0), 0)

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Gestion des coûts</h1>
          <p className="text-muted">
            Analysez les coûts et les prévisions d'utilisation des modèles LLM
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Header>Filtres</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date de début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Grouper par</Form.Label>
                <Form.Select
                  value={filters.groupBy}
                  onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                >
                  <option value="day">Jour</option>
                  <option value="week">Semaine</option>
                  <option value="month">Mois</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button variant="primary" onClick={handleApplyFilters} className="w-100">
                Appliquer
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-primary">${totalCost.toFixed(2)}</Card.Title>
                  <Card.Text className="text-muted">Coût total (période)</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-success">${avgDailyCost.toFixed(2)}</Card.Title>
                  <Card.Text className="text-muted">Moyenne journalière</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-warning">${projectedMonthlyCost.toFixed(2)}</Card.Title>
                  <Card.Text className="text-muted">Projection mensuelle</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-info">${forecastTotal.toFixed(2)}</Card.Title>
                  <Card.Text className="text-muted">Prévision 30 jours</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Cost Chart with Forecast */}
          <Card className="mb-4">
            <Card.Body>
              <CostChart
                data={costData}
                forecast={forecastData}
                title="Évolution des coûts et prévisions"
              />
            </Card.Body>
          </Card>

          {/* Cost Breakdown by Model */}
          <Card>
            <Card.Header>Répartition des coûts par modèle</Card.Header>
            <Card.Body>
              {breakdown.length === 0 ? (
                <p className="text-muted text-center py-3">Aucune donnée disponible</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Modèle</th>
                      <th>Fournisseur</th>
                      <th>Requêtes</th>
                      <th>Tokens entrée</th>
                      <th>Tokens sortie</th>
                      <th>Coût total</th>
                      <th>% du total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.map((item) => (
                      <tr key={item.model_id || item.model}>
                        <td>{item.name || item.model}</td>
                        <td>{item.provider}</td>
                        <td>{(item.request_count || 0).toLocaleString()}</td>
                        <td>{(item.input_tokens || 0).toLocaleString()}</td>
                        <td>{(item.output_tokens || 0).toLocaleString()}</td>
                        <td className="text-end">${(item.cost || 0).toFixed(4)}</td>
                        <td className="text-end">
                          {totalCost > 0 ? ((item.cost / totalCost) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-secondary">
                      <th colSpan={5}>Total</th>
                      <th className="text-end">${totalCost.toFixed(2)}</th>
                      <th className="text-end">100%</th>
                    </tr>
                  </tfoot>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  )
}

export default Costs
